import merge from 'deepmerge';
import {putAdviserInCache} from 'hooks/advisers';
import {fetchAdviseSession} from 'providers/core/adviseSessions';
import {useEffect} from 'react';
import {queryCache, useQuery} from 'react-query';
import {overwriteMerge} from 'utils/deep-merge';
import {showSignboard} from '../views/extera/signboard';

const staleTime = 5 * 60 * 1000;

const queryFn = async (id) => {
  try {
    const {adviseSession} = await fetchAdviseSession(id);
    return adviseSession;
  } catch (e) {
    if (e.code === 'INVALID_REQUEST' || e.code === 'NOT_FOUND') {
      return null;
    }
    throw e;
  }
};

export function useAdviseSession(id, forceUpdate = false) {
  const queryKey = ['adviseSession', id];

  useEffect(() => {
    if (forceUpdate) {
      queryCache.invalidateQueries(queryKey, {
        exact: true,
        refetchInactive: true,
      });
    }
  }, [forceUpdate]); // eslint-disable-line react-hooks/exhaustive-deps

  return useQuery(queryKey, () => queryFn(id), {
    staleTime,
  });
}

export function putAdviseSessionInCache(adviseSession) {
  const queryKey = ['adviseSession', adviseSession.id];
  queryCache.setQueryData(queryKey, adviseSession, {
    staleTime,
  });

  if (adviseSession.adviser) {
    putAdviserInCache(adviseSession.adviser);
  }

  try {
    if (adviseSession?.status === 'ACCEPTED') {
      const time = Date.now() / 1000;
      const inWindow =
        time >= adviseSession.startAt &&
        time <=
          adviseSession.startAt + Math.min(adviseSession.duration, 90) * 60;

      showSignboard(
        inWindow,
        adviseSession.id,
        adviseSession.adviser?.firstName +
          ' ' +
          adviseSession.adviser?.lastName ?? '',
        adviseSession?.fromUser?.nickname ?? '',
        adviseSession.type ?? '',
        adviseSession.duration ?? 0,
        adviseSession.adviser?.avatar?.avatar ?? '',
        adviseSession.adviser?.avatar?.thumbnail ?? '',
        adviseSession.startAt ?? 0,
        adviseSession?.adviser?.skills?.[0]?.title ?? '',
      );
    } else {
      showSignboard(false, adviseSession.id);
    }
  } catch (e) {
    console.error('error in showSignboard logic', e);
  }
}

export function updateAdviseSessionInCache(id, payload) {
  const queryKey = ['adviseSession', id];
  const data = queryCache.getQueryData(queryKey);

  if (data) {
    putAdviseSessionInCache(
      merge(data, payload, {
        arrayMerge: overwriteMerge,
      }),
    );
  }
}

export function clearAdviseSessionInCache(id) {
  const queryKey = ['adviseSession', id];

  queryCache.setQueryData(queryKey, null, {
    staleTime,
  });
}

export function getAdviseSession(id) {
  const queryKey = ['adviseSession', id];

  return queryCache.prefetchQuery(
    queryKey,
    () => queryFn(id),
    {
      staleTime,
    },
    {
      throwOnError: true,
    },
  );
}
